import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { useGlobalNavigationStore } from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import { shallowComparison } from "client/Misc/compareUtils";
import { useSpaceNavigationHandler } from "../Layout/GlobalNavigationSidebar/SubNavigationContent/useSpaceNavigationHandler";
import { useCreateSpaceDialog } from "./CreateSpaceDialog";
import { useWorkspaceControllerStore, WorkspaceController } from "./useWorkspaceControllerStore";
import { SavedSpace } from "./EditSpaceDialog/EditSpaceDialog";
import { SpaceAccessKind, SpaceKind } from "client/Api/Workspace/workspace";
import {
	WorkspaceEvent,
	publish as workspacePublish
} from "./events";
import { isNotNil } from "client/Misc/utils";

export type startCreateSpaceOptions = {
	shouldPreventDeactivation?: boolean;
};

export type StartCreateSpaceResult = [
	element: ReactElement | null,
	startCreateSpace: (shouldPreventDeactivation?: boolean) => Promise<TryResult<SavedSpace>>
];

export function useStartCreateSpace(): StartCreateSpaceResult {
	const {
		mode: globalNavigationBarMode,
		workspaceSpaceNavigationHandlerOptions
	} = useGlobalNavigationStore(
		x => ({
			mode: x.mode,
			workspaceSpaceNavigationHandlerOptions: x.workspaceSpaceNavigationHandlerOptions
		}),
		shallowComparison
	);

	const [spaceNavigationElement, spaceNavigationHandler] = useSpaceNavigationHandler(workspaceSpaceNavigationHandlerOptions);
	const [createSpaceDialog, openCreateSpaceDialog] = useCreateSpaceDialog();
	const workspaceController: OptionalModel<WorkspaceController> = useWorkspaceControllerStore(x => x.controller);

	const startCreateSpaceHandler = useCallback(
		async (shouldPreventDeactivation: boolean = true): Promise<TryResult<SavedSpace>> => {
			if (shouldPreventDeactivation
				&& !await spaceNavigationHandler.onPreventDeactivation(globalNavigationBarMode)
			) {
				return { wasSuccessful: false };
			}

			const result: TryResult<SavedSpace> = await openCreateSpaceDialog({});

			if (!result.wasSuccessful) {
				return { wasSuccessful: false };
			}

			const savedSpace: SavedSpace = result.result;

			if (savedSpace.kind !== SpaceKind.Shared) {
				return { wasSuccessful: false };
			}

			workspacePublish(
				WorkspaceEvent.SpaceAdded,
				{
					space: {
						kind: savedSpace.kind,
						id: savedSpace.id,
						name: savedSpace.name,
						accessKind: savedSpace.userInfo.canEdit
							? SpaceAccessKind.Edit
							: SpaceAccessKind.Access
					}
				}
			);

			if (workspaceController.isSet) {
				// Not ideal as you probably want to start editing the new space, but without
				// cancelling edit mode, activating the new item will once again show SaveSpaceChangesBeforeSwitchDialog
				// in the current implementation
				workspaceController
					.value
					.cancelEditMode();

				workspaceController
					.value
					.refresh();

				// Navigation is handled inside the workspace
				// because spaceNavigationHandler doesn't have
				// the new space yet at this point
			} else {
				spaceNavigationHandler.activate(savedSpace.id);
			}

			return {
				wasSuccessful: true,
				result: savedSpace
			};
		},
		[
			globalNavigationBarMode,
			spaceNavigationHandler,
			workspaceController,
			openCreateSpaceDialog
		]
	);

	const element = useMemo(
		(): ReactElement | null => {
			return [
				spaceNavigationElement,
				createSpaceDialog
			].find(isNotNil) ?? null;
		},
		[
			createSpaceDialog,
			spaceNavigationElement
		]
	);

	return useMemo(
		(): StartCreateSpaceResult => ([
			element,
			startCreateSpaceHandler
		]),
		[element, startCreateSpaceHandler]
	);
}